/** @format */

/* eslint react-hooks/rules-of-hooks: "warn" */
import isPropValid from '@emotion/is-prop-valid';
import { useSpring } from '@react-spring/web';
import { useTranslation } from 'next-i18next';
import React, {
    FunctionComponent,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import { MediaQuery } from '@toucan/helpers/media-queries';

import Arrow from '@toucan/icons/legacy/arrow-right.svg';
import SmallArrowIcon from '@toucan/icons/legacy/small-arrow.svg';

import {
    IconButton,
    IconButtonFill,
    IconButtonSize, // eslint-disable-next-line boundaries/element-types -- This should be using the navigation icon from feather
} from '@toucan/client/shared/components/icon-button';
// eslint-disable-next-line boundaries/element-types -- This should be in feather
import { useMediaQuery } from '@toucan/client/shared/hooks/use-media-query';

import { sum } from './helpers';
import {
    GRADIENT_WIDTH_DESKTOP,
    GRADIENT_WIDTH_MOBILE,
    styles,
} from './scrollable-carousel.styles';

export enum ScrollableCarouselButtonSize {
    SMALL = 'small',
    MEDIUM = 'medium',
}

export type ScrollableCarouselProps = {
    /**
     * The items to show in the carousel
     */
    items: ReactNode[];

    /**
     * Padding applied to the left of the carousel.
     * Used to align the start items of the carousel to the wrapper
     *
     * @default 0
     */
    paddingLeft?: number;

    /**
     * An optional size for the carousel's buttons. One of:
     * 1. SMALL
     * 2. MEDIUM
     *
     * @default MEDIUM
     */
    buttonSize?: ScrollableCarouselButtonSize;

    /**
     * Whether to show buttons
     *
     * @default false
     */
    showButtons?: boolean;

    /**
     * The width to set each item to. If not set the width will
     * be whatever the content sizes it to
     */
    itemWidth?: number;

    /**
     * This option can be passed to remove the previous/ next buttons from the
     * tab index
     *
     *
     * @default false
     */
    removeButtonTabIndex?: boolean;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * The number of pixels the item can be out of view by
     * and still count as in view
     */
    itemOffset?: number;
    initialItemIndex?: number;
    'data-id'?: string;
};

export type StyledScrollableCarouselProps = ScrollableCarouselProps & {
    showNextButton: boolean;
    showPrevButton: boolean;
};

const StyledScrollableCarousel = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

type ScrollableCarouselHelperArgs = Pick<
    ScrollableCarouselProps,
    'itemWidth' | 'paddingLeft' | 'itemOffset' | 'initialItemIndex'
> & {
    itemCount: number;
};

/**
 * Contains the logic for the scrollable carousel
 */
const useScrollableCarouselHelper = ({
    itemWidth,
    itemCount,
    itemOffset = 50,
    initialItemIndex = 0,
}: ScrollableCarouselHelperArgs) => {
    const outerRef = useRef<HTMLDivElement | null>(null);

    const [isCarouselActive, setCarouselActive] = useState<boolean>(false);
    const [showPrevButton, setShowPrevButton] = useState<boolean>(false);
    const [showNextButton, setShowNextButton] = useState<boolean>(false);

    // Scroll left value of .scrollable-carousel-outer
    const scrollLeft = useRef<number>(0);
    const paddingLeft = useRef<number>(0);
    const paddingRight = useRef<number>(0);
    const scrollableCarouselWidth = useRef<number>(0);
    const itemWidths = useRef<number[]>([]);
    const isMobile = useMediaQuery(MediaQuery.MOBILE);
    const GRADIENT_WIDTH = isMobile
        ? GRADIENT_WIDTH_MOBILE
        : GRADIENT_WIDTH_DESKTOP;

    // Used to stop the user scrolling when the scroll position is being animated
    const lockScroll = useRef<boolean>(false);

    const [, setX] = useSpring(() => ({ x: scrollLeft.current }));

    /**
     * Determine whether the contents of the carousel fill the stage area
     */
    function isStageFilled() {
        const constraint = paddingLeft.current * 2;
        const constrainedWidth = scrollableCarouselWidth.current - constraint;
        const innerWidth = sum(itemWidths.current);

        return innerWidth >= constrainedWidth;
    }

    /**
     * Gets the width of each item in the ScrollableCarousel
     *
     * @returns An array of item widths
     */
    function measureItems() {
        if (!outerRef.current) {
            throw new Error('no outer ref');
        }

        const computedOuterStyle = window.getComputedStyle(outerRef.current);
        paddingLeft.current = parseInt(computedOuterStyle.paddingLeft) || 0;
        paddingRight.current = parseInt(computedOuterStyle.paddingRight) || 0;

        const items = Array.from(
            outerRef.current.querySelectorAll('.scrollable-carousel-item')
        );

        const widths = items.map((element) => {
            return element.getBoundingClientRect().width;
        });

        return widths;
    }

    /**
     * Gets the button visibility states
     */
    function getButtonState() {
        const itemWidthSum = sum(itemWidths.current);
        const showPrevButton = scrollLeft.current > itemOffset;
        const showNextButton =
            scrollableCarouselWidth.current +
                scrollLeft.current -
                paddingLeft.current -
                paddingRight.current <
            itemWidthSum - itemOffset;

        return {
            showPrevButton,
            showNextButton,
        };
    }

    /**
     * Updates the buttons states
     */
    function updateButtonState() {
        const { showNextButton, showPrevButton } = getButtonState();

        setShowPrevButton(showPrevButton);
        setShowNextButton(showNextButton);
    }

    /**
     * Stores the ScrollableCarousel & it's items width
     */
    function handleWindowResize() {
        if (!outerRef.current) {
            throw new Error('no outer ref');
        }

        const containerWidth = outerRef.current.getBoundingClientRect().width;

        itemWidths.current = measureItems();
        scrollableCarouselWidth.current = containerWidth;
        setCarouselActive(isStageFilled());
        updateButtonState();

        if (initialItemIndex <= 0) {
            return;
        }

        if (initialItemIndex >= itemCount - 1) {
            outerRef.current?.scrollTo?.({ left: containerWidth });
            return;
        }

        const itemsWidth = sum(itemWidths.current);

        outerRef.current?.scrollTo?.({
            left: paddingLeft.current + (itemsWidth - containerWidth) / 2,
        });
    }

    /**
     * Gets the indexes of the ScrollableCarousel items that are currently in view
     */
    function getIndexesInView() {
        const { showNextButton, showPrevButton } = getButtonState();
        const indexesInView: number[] = [];
        const minX =
            scrollLeft.current -
            itemOffset -
            paddingLeft.current +
            (showPrevButton ? GRADIENT_WIDTH : 0);

        const maxX =
            scrollLeft.current +
            scrollableCarouselWidth.current +
            (itemOffset - paddingLeft.current) -
            (showNextButton ? GRADIENT_WIDTH : 0);

        itemWidths.current.forEach((width, index) => {
            const previousItemsWidth = sum(itemWidths.current.slice(0, index));
            const itemXStart = previousItemsWidth;
            const itemXEnd = previousItemsWidth + width;

            if (itemXStart >= minX && itemXEnd <= maxX) {
                indexesInView.push(index);
            }
        });

        return indexesInView;
    }

    /**
     * Gets the scroll left value that would put the item in view
     *
     * @param {number} index The index of the item
     * @param {boolean} lastOnPage If the item should be last on the page
     */
    function getScrollLeft(index: number, lastOnPage: boolean) {
        if (lastOnPage) {
            const maxX =
                sum(itemWidths.current.slice(0, index + 1)) +
                paddingLeft.current +
                GRADIENT_WIDTH;

            const scrollLeft = Math.max(
                maxX - scrollableCarouselWidth.current,
                0
            );

            return scrollLeft;
        }

        const maxScrollLeft =
            sum(itemWidths.current) -
            scrollableCarouselWidth.current +
            paddingLeft.current +
            paddingRight.current;

        const minX =
            sum(itemWidths.current.slice(0, index)) +
            paddingLeft.current -
            GRADIENT_WIDTH;

        const scrollLeft = minX < maxScrollLeft ? minX : maxScrollLeft;
        return scrollLeft;
    }

    /**
     * Scrolls to the new position using react spring to animate it
     *
     * @param currentX The current value of x
     * @param newX The new value of x
     */
    function setScrollPosition(currentX: number, newX: number) {
        const setXProps: any = {
            to: { x: newX },
            reset: true,
            from: { x: currentX },
            onChange: (props: any) => {
                lockScroll.current = true;
                if (outerRef.current) {
                    outerRef.current.scrollLeft = props.value.x;
                    outerRef.current.style.pointerEvents = 'none';
                }
            },
            onRest: () => {
                lockScroll.current = false;
                if (outerRef.current) {
                    scrollLeft.current = outerRef.current.scrollLeft;
                    outerRef.current.style.pointerEvents = '';
                }
            },
        };

        setX.start(setXProps);
    }

    /**
     * Handles what the ScrollableCarousel should do when the previous button is clicked
     */
    const handlePrevClick = useCallback(() => {
        const indexesInView = getIndexesInView();
        const prevIndex = indexesInView[0] - 1;
        const newScrollLeft =
            prevIndex < 0 ? 0 : getScrollLeft(prevIndex, true);

        setScrollPosition(scrollLeft.current, newScrollLeft);
    }, [getIndexesInView, getScrollLeft, setScrollPosition]);

    /**
     * Handles what the ScrollableCarousel should do when the next button is clicked
     */
    const handleNextClick = useCallback(() => {
        const indexesInView = getIndexesInView();
        const nextIndex = indexesInView[indexesInView.length - 1] + 1;

        const newScrollLeft =
            nextIndex + 1 > itemWidths.current.length
                ? sum(itemWidths.current) - scrollableCarouselWidth.current
                : getScrollLeft(nextIndex, false);

        setScrollPosition(scrollLeft.current, newScrollLeft);
    }, [getIndexesInView, getScrollLeft, setScrollPosition]);

    /**
     * Sets internal state when .scrollable-carousel-outer is scrolled
     */
    function handleOuterScroll(e: Event) {
        if (lockScroll.current === true) {
            e.preventDefault();
        }

        if (outerRef.current) {
            scrollLeft.current = outerRef.current.scrollLeft;
            updateButtonState();
        }
    }

    useEffect(() => {
        if (outerRef.current) {
            handleWindowResize();
            outerRef.current.addEventListener('scroll', handleOuterScroll);
        }
        window.addEventListener('resize', handleWindowResize);

        return () => {
            outerRef.current?.removeEventListener('scroll', handleOuterScroll);
            window.removeEventListener('resize', handleWindowResize);
        };
    }, []);

    // Update measurements when either of these props change
    useEffect(() => {
        if (outerRef.current) {
            handleWindowResize();
        }
    }, [itemWidth, paddingLeft.current, itemCount]);

    // Reset everything if contents no longer fill the carousel container
    useEffect(() => {
        if (!isCarouselActive) {
            if (outerRef.current) {
                outerRef.current.scrollLeft = 0;
            }

            scrollLeft.current = 0;
            setScrollPosition(0, 0);
        }
    }, [isCarouselActive]);

    return {
        showPrevButton,
        showNextButton,
        handlePrevClick,
        handleNextClick,
        isCarouselActive,
        isMobile,
        ref: outerRef,
    };
};

export const ScrollableCarousel: FunctionComponent<ScrollableCarouselProps> = ({
    items,
    itemWidth,
    buttonSize = ScrollableCarouselButtonSize.MEDIUM,
    removeButtonTabIndex = false,
    showButtons = false,
    paddingLeft = 0,
    itemOffset,
    initialItemIndex,
    'data-id': dataId = 'scrollable-carousel',
    ...otherProps
}: ScrollableCarouselProps) => {
    const { t } = useTranslation();
    const {
        isCarouselActive,
        ref,
        showPrevButton,
        showNextButton,
        handlePrevClick,
        handleNextClick,
    } = useScrollableCarouselHelper({
        itemCount: items.length,
        itemOffset,
        initialItemIndex,
    });

    /**
     * @returns {ReactNode} The previous arrow as a react node
     */
    function getPreviousArrow(): ReactNode {
        const icon =
            buttonSize === ScrollableCarouselButtonSize.SMALL ? (
                <SmallArrowIcon />
            ) : (
                <Arrow />
            );

        const iconButtonSize =
            buttonSize === ScrollableCarouselButtonSize.SMALL
                ? IconButtonSize.SMALL
                : IconButtonSize.EXTRA_LARGE;

        return (
            <IconButton
                className="scrollable-carousel-arrow scrollable-carousel-arrow--previous"
                label={t('Previous')}
                size={iconButtonSize}
                fill={IconButtonFill.HOLLOW}
                border={false}
                onClick={handlePrevClick}
                tabIndex={removeButtonTabIndex ? -1 : 0}
                data-testid="scrollable-carousel__prev"
                data-id={`${dataId}__prev`}
            >
                {icon}
            </IconButton>
        );
    }

    /**
     * @returns {ReactNode} The next arrow as a react node
     */
    function getNextArrow(): ReactNode {
        const icon =
            buttonSize === ScrollableCarouselButtonSize.SMALL ? (
                <SmallArrowIcon />
            ) : (
                <Arrow />
            );

        const iconButtonSize =
            buttonSize === ScrollableCarouselButtonSize.SMALL
                ? IconButtonSize.SMALL
                : IconButtonSize.EXTRA_LARGE;

        return (
            <IconButton
                className="scrollable-carousel-arrow scrollable-carousel-arrow--next"
                label={t('Next')}
                size={iconButtonSize}
                fill={IconButtonFill.HOLLOW}
                border={false}
                onClick={handleNextClick}
                tabIndex={removeButtonTabIndex ? -1 : 0}
                data-testid="scrollable-carousel__next"
                data-id={`${dataId}__next`}
            >
                {icon}
            </IconButton>
        );
    }

    // Wraps the items in the required markup
    const itemsWithMarkup = items.map((item, i) => {
        const styleObject: any = {};
        if (itemWidth) {
            styleObject.width = `${itemWidth}px`;
        }
        return (
            <li
                key={i}
                className="scrollable-carousel-item"
                style={styleObject}
                data-id={`${dataId}__item-${i}`}
            >
                {item}
            </li>
        );
    });

    return (
        <StyledScrollableCarousel
            items={items}
            paddingLeft={isCarouselActive ? paddingLeft : 0}
            showNextButton={showNextButton}
            showPrevButton={showPrevButton}
            buttonSize={buttonSize}
            data-id={dataId}
            {...otherProps}
        >
            <div
                className="scrollable-carousel-outer"
                ref={ref}
                data-testid="scrollable-carousel__outer"
                data-id={`${dataId}__outer`}
            >
                <div
                    className="scrollable-carousel-inner"
                    data-id={`${dataId}__inner`}
                >
                    <div data-id={`${dataId}__list-container`}>
                        <ul
                            className="scrollable-carousel-list"
                            data-id={`${dataId}__list`}
                        >
                            {itemsWithMarkup}
                        </ul>
                    </div>
                </div>
            </div>

            {isCarouselActive &&
                showPrevButton &&
                showButtons &&
                getPreviousArrow()}
            {isCarouselActive &&
                showNextButton &&
                showButtons &&
                getNextArrow()}
        </StyledScrollableCarousel>
    );
};
